/**
 *
 * @module chromecastPlayerAdapter
 * @author Greg Babula [greg.babula@disneystreaming.com]
 * @author Eric Milliren [eric.milliren@disneystreaming.com]
 * @desc PlayerAdapter for Chromecast
 * @see https://github.bamtech.co/sdk-doc/spec-sdk/blob/master/specs/feature_overviews/media.md
 * @see https://github.bamtech.co/sdk-doc/spec-sdk/blob/master/specs/feature_overviews/stream-sample.md
 * @see https://developer.mozilla.org/en-US/docs/Web/API/HTMLMediaElement
 * @see https://github.bamtech.co/fed-core/browser-sdk/blob/master/docs/reference/PlayerEvents.md
 * @see https://github.bamtech.co/fed-core/browser-sdk/blob/master/docs/reference/PlayerProperties.md
 * @see https://developers.google.com/cast/docs/reference/player/cast.player.api.Host
 *
 */

import Check from 'check-types';
import Services from './../../services/entry';
import Drm from './../../drm/entry';
import PlayerAdapter from './../playerAdapter';
import PlaybackMetrics from './../playbackMetrics';
import PlaybackEventListener from './../playbackEventListener';

const Util = Services.Util;
const DrmProvider = Drm.DrmProvider;
const DrmType = Services.Media.DrmType;

const ErrorReason = Services.Exception.ErrorReason;
const ExceptionReference = Services.Exception.ExceptionReference;
const ServiceException = Services.Exception.ServiceException;
const InvalidArgumentException = Services.Exception.InvalidArgumentException;

const {
    object,
    instanceStrict,
    nonEmptyString
} = Check.assert;

/**
 *
 * @class ChromecastPlayerAdapter
 * @extends {PlayerAdapter}
 * @desc Chromecast Player Adapter. Note: this uses the
 * updateLicenseRequestInfo callback to set license
 *
 */
export default class ChromecastPlayerAdapter extends PlayerAdapter {

    /**
     *
     * @param {NativePlayer} nativePlayer
     * @param {cast.player.api} playerApi
     * @throws {InvalidArgumentException}
     * @note The ChromecastPlayerAdapter requires nativePlayer.getHost
     *
     */
    constructor(nativePlayer, playerApi) {

        const errorMessage = {
            ctor: 'ChromecastPlayerAdapter(nativePlayer, playerApi)',
            get nativePlayer() {
                return `${this.ctor} needs NativePlayer instance`;
            },
            get playerApi() {
                return `${this.ctor} needs cast.player.api instance`;
            }
        };

        Check.assert.function(Util.getSafe(() => nativePlayer.getHost), errorMessage.nativePlayer, InvalidArgumentException); // eslint-disable-line
        object(playerApi, errorMessage.playerApi, InvalidArgumentException);

        super(nativePlayer);

        /**
         *
         * @access private
         * @type {Object}
         *
         */
        this.host = nativePlayer.getHost();

        /**
         *
         * @access private
         * @type {cast.player.api}
         *
         */
        this.playerApi = playerApi;

        /**
         *
         * @access private
         * @type {Object}
         * @desc The {HTMLMediaElement} tied to the nativePlayer instance.
         *
         */
        this.mediaElement = this.host.mediaElement;

        /**
         *
         * @access public
         * @since 3.2.0
         * @type {cast.player.api.HlsSegmentFormat}
         *
         */
        this.audioSegmentFormat = undefined;

        /**
         *
         * @access public
         * @since 3.2.0
         * @type {cast.player.api.HlsVideoSegmentFormat}
         *
         */
        this.videoSegmentFormat = undefined;

    }

    /**
     *
     * @access public
     * @param {String} playlistUri - The URL to be used during playback.
     * @desc Callback used when prepare has been called (usually via the {PlaybackSession}).
     * Sets the source URI on the {NativePlayer} instance.
     * @throws {InvalidStateException} Unable to set playlistUri on NativePlayer
     * @returns {Promise<Void>}
     *
     */
    setSource(playlistUri) {

        const errorMessage = {
            method: 'ChromecastPlayerAdapter.setSource(playlistUri)',
            get playlistUri() {
                return `${this.method} uri should be a String with length`;
            },
            get setPlaylistUri() {
                return `${this.method} unable to set playlistUri on NativePlayer`;
            }
        };

        nonEmptyString(playlistUri, errorMessage.playlistUri, InvalidArgumentException);

        const { host } = this;

        return new Promise((resolve, reject) => {

            this.playlistUri = playlistUri;

            try {

                host.url = playlistUri;
                host.mediaElement.setAttribute('crossorigin', 'use-credentials');

                return resolve();

            } catch (ex) {

                const reasons = [new ErrorReason('', `${errorMessage.setPlaylistUri} - ${ex}`)];
                const exceptionData = ExceptionReference.common.invalidState;

                return reject(new ServiceException({ reasons, exceptionData }));

            }

        });

    }

    /**
     *
     * @access public
     * @since 2.0.0
     * @desc Gets a snapshot of information about media playback.
     * @throws {InvalidStateException} Unable to get NativePlayer playhead data
     * @returns {Promise<PlaybackMetrics|InvalidStateException>} - instance that contains a snapshot
     * of information about media playback.
     * @note nativePlayer.currentTime - The HTMLMediaElement.currentTime property gives
     * the current playback time in seconds
     * @note metric value is rounded down to prevent possible service issues with floats
     * @note executed by {PlaybackTelemetryDispatcher#recordStreamSample}
     *
     */
    getPlaybackMetrics() {

        const errorMessage = {
            method: 'ChromecastPlayerAdapter.getPlaybackMetrics()',
            get currentPlayhead() {
                return `${this.method} unable to get NativePlayer playhead data`;
            }
        };

        const { mediaElement, unavailableMetric } = this;

        if (Check.not.assigned(mediaElement.currentTime)) {

            const reasons = [new ErrorReason('', errorMessage.currentPlayhead)];
            const exceptionData = ExceptionReference.common.invalidState;

            return Promise.reject(new ServiceException({ reasons, exceptionData }));

        }

        const currentBitrate = unavailableMetric;
        const currentPlayhead = Math.floor(mediaElement.currentTime) || unavailableMetric;

        return Promise.resolve(new PlaybackMetrics(currentBitrate, currentPlayhead));

    }

    /**
     *
     * @access public
     * @since 2.0.0
     * @param {PlaybackEventListener} listener
     * @throws {InvalidStateException} Unable to add PlaybackEventListener
     * @returns {Promise<Void>}
     *
     */
    addListener(listener) {

        const errorMessage = {
            method: 'ChromecastPlayerAdapter.addListener(listener)',
            get listener() {
                return `${this.method} needs PlaybackEventListener instance`;
            },
            get addListener() {
                return `${this.method} unable to add PlaybackEventListener`;
            }
        };

        instanceStrict(listener, PlaybackEventListener, errorMessage.listener, InvalidArgumentException);

        const { boundHandlers, mediaElement } = this;
        const { onPlay, onPause, onError, onPlayedToCompletion } = this;

        return new Promise((resolve, reject) => {

            if (Check.function(mediaElement.play)) {

                this.listener = listener;

                boundHandlers.onPlay = onPlay.bind(this);
                boundHandlers.onPause = onPause.bind(this);
                boundHandlers.onError = onError.bind(this);
                boundHandlers.onPlayedToCompletion = onPlayedToCompletion.bind(this);

                mediaElement.onplay = boundHandlers.onPlay;
                mediaElement.onpause = boundHandlers.onPause;
                mediaElement.onerror = boundHandlers.onError;
                mediaElement.onended = boundHandlers.onPlayedToCompletion;

                return resolve();

            }

            const reasons = [new ErrorReason('', errorMessage.addListener)];
            const exceptionData = ExceptionReference.common.invalidState;

            return reject(new ServiceException({ reasons, exceptionData }));

        });

    }

    /**
     *
     * @access public
     * @since 2.0.0
     * @param {PlaybackEventListener} listener
     * @returns {Promise<Void>}
     *
     */
    removeListener(listener) {

        const errorMessage = {
            method: 'ChromecastPlayerAdapter.removeListener(listener)',
            get listener() {
                return `${this.method} needs PlaybackEventListener instance`;
            }
        };

        instanceStrict(listener, PlaybackEventListener, errorMessage.listener, InvalidArgumentException);

        const { mediaElement } = this;

        return new Promise((resolve) => {

            if (Check.function(mediaElement.play) && Check.equal(this.listener, listener)) {

                this.listener = null;

                mediaElement.onplay = null;
                mediaElement.onpause = null;
                mediaElement.onerror = null;
                mediaElement.onended = null;

            }

            return resolve();

        });

    }

    /**
     *
     * @access public
     * @since 3.2.0
     * @param {SDK.Drm.DrmProvider} drmProvider
     * @returns {Promise<Void>}
     *
     */
    setDrmProvider(drmProvider) {

        const errorMessage = {
            method: 'ChromecastPlayerAdapter.setDrmProvider(drmProvider)',
            get drmProvider() {
                return `${this.method} needs SDK.Drm.DrmProvider instance`;
            }
        };

        instanceStrict(drmProvider, DrmProvider, errorMessage.drmProvider, InvalidArgumentException);

        this.drmProvider = drmProvider;

        return this.attach();

    }

    /**
     *
     * @access public
     * @param {Array<String>} audioSegmentTypes
     * @param {Array<String>} videoSegmentTypes
     * @since 3.2.0
     * @returns {Promise<Void>}
     *
     */
    updateSegmentFormat(audioSegmentTypes, videoSegmentTypes) {

        this.updateAudioSegmentFormat(audioSegmentTypes).updateVideoSegmentFormat(videoSegmentTypes);

        return Promise.resolve();

    }

    /**
     *
     * @access public
     * @desc Resets player adapter state. Removes all playback listeners.
     * @returns {Promise<Void>}
     *
     */
    clean() {

        const { listener } = this;

        this.playlistUri = '';
        this.boundHandlers = {};
        this.drmProvider = null;
        this.audioSegmentFormat = undefined;
        this.videoSegmentFormat = undefined;

        if (Check.assigned(listener)) {
            return this.removeListener(listener);
        }

        return Promise.resolve();

    }

    /**
     *
     * @access private
     * @since 3.6.0
     * @param {Object} adEngineData
     * @desc assigns adEngine data from the playlist service, some of which should be included in segment requests
     * @note if adEngineData is unassigned do not do anything
     *
     */
    setAdEngineData(adEngineData) {

        this.adEngineData = adEngineData;

        if (Check.nonEmptyObject(adEngineData)) {

            const { host } = this;
            const { ssess } = adEngineData;

            host.updateSegmentRequestInfo = (requestInfo) => {

                const isAdEngineCall = Util.adEngineRegex(requestInfo.url);

                // need to reset these for each segment request
                requestInfo.headers = null;
                requestInfo.crossDomain = undefined;
                requestInfo.withCredentials = false;

                if (ssess && isAdEngineCall) {

                    requestInfo.headers = {};
                    requestInfo.headers.ssess = ssess;
                    requestInfo.crossDomain = true;
                    requestInfo.withCredentials = true;

                }

            };

        }

    }

    /* #region private: */

    /**
     *
     * @access public
     * @since 3.1.0
     * @desc Completes the cleanup process by completely cleaning up all `PlayerAdapter`
     * references. This method should be executed by the application developer
     * when they no longer need to use a `PlayerAdapter` instance.
     * @note Because `clean` is automatically called by the `PlaybackSession` instance,
     * another method is necessary to handle the use case of an application developer who
     * wants to continue using a `PlayerAdapter` instance after a `PlaybackSession` has been released.
     * @returns {Promise<Void>}
     *
     */
    dispose() {

        this.nativePlayer = null;
        this.accessToken = null;
        this.host = null;
        this.mediaElement = null;

        return Promise.resolve();

    }

    /**
     *
     * @access private
     * @since 3.2.0
     * @returns {Promise<Void>}
     *
     */
    attach() {

        const { drmProvider, playerApi } = this;

        if (Check.equal(drmProvider.type, DrmType.WIDEVINE)) {
            return this.attachDrm(Util.getSafe(() => playerApi.ContentProtection.WIDEVINE));
        }

        if (Check.equal(drmProvider.type, DrmType.PLAYREADY)) {
            return this.attachDrm(Util.getSafe(() => playerApi.ContentProtection.PLAYREADY));
        }

        return this.attachSilkDrm();

    }

    /**
     *
     * @access private
     * @since 3.2.0
     * @param {cast.player.api.ContentProtection} protectionSystem
     * @returns {Promise<Void>}
     * @note Uses undocument flag (disableSourceBufferTimeAdjust) provided by Google to
     * enable bypassing gap in cmaf segments when set to true
     *
     */
    attachDrm(protectionSystem) {

        const { host, drmProvider } = this;

        host.licenseUrl = drmProvider.licenseRequestUri;
        host.protectionSystem = protectionSystem;
        host.disableSourceBufferTimeAdjust = true;

        host.updateLicenseRequestInfo = (requestInfo) => {

            requestInfo.crossDomain = true;
            requestInfo.withCredentials = true;
            requestInfo.headers = drmProvider.processLicenseRequestHeaders() || {};

        };

        return Promise.resolve();

    }

    /**
     *
     * @access private
     * @since 3.2.0
     * @returns {Promise<Void>}
     *
     */
    attachSilkDrm() {

        const { host, accessToken } = this;

        host.disableSourceBufferTimeAdjust = false;

        host.updateLicenseRequestInfo = (requestInfo) => {

            requestInfo.withCredentials = true;
            requestInfo.headers = {};
            requestInfo.headers.Authorization = accessToken;

        };

        return Promise.resolve();
    }

    /**
     *
     * @access private
     * @param {Array<String>} audioSegmentTypes
     * @since 3.2.0
     * @returns {this}
     *
     */
    updateAudioSegmentFormat(audioSegmentTypes) {

        const { playerApi } = this;

        this.audioSegmentFormat = undefined;

        if (Check.nonEmptyArray(audioSegmentTypes)) {

            if (audioSegmentTypes.includes('FMP4')) {
                this.audioSegmentFormat = Util.getSafe(() => playerApi.HlsSegmentFormat.FMP4);
                return this;
            }

            if (audioSegmentTypes.includes('PACKED_AUDIO')) {
                this.audioSegmentFormat = Util.getSafe(() => playerApi.HlsSegmentFormat.TS_AAC);
                return this;
            }

        }

        return this;

    }

    /**
     *
     * @access private
     * @param {Array<String>} videoSegmentTypes
     * @since 3.2.0
     * @returns {this}
     *
     */
    updateVideoSegmentFormat(videoSegmentTypes) {

        const { playerApi } = this;

        this.videoSegmentFormat = undefined;

        if (Check.nonEmptyArray(videoSegmentTypes)) {

            if (videoSegmentTypes.includes('FMP4')) {
                this.videoSegmentFormat = Util.getSafe(() => playerApi.HlsVideoSegmentFormat.FMP4);
                return this;
            }

            if (videoSegmentTypes.includes('TS')) {
                this.videoSegmentFormat = Util.getSafe(() => playerApi.HlsVideoSegmentFormat.MPEG2_TS);
                return this;
            }

        }

        return this;

    }

}



// WEBPACK FOOTER //
// ./src/media/playerAdapter/chromecastPlayerAdapter.js